/**
 * Policies domain.
 *
 * A school-level document library — official policies uploaded as PDFs,
 * versioned, approved by the governing body, and signed/acknowledged by
 * parents and staff. Unlike every other domain, policies are NOT per-student
 * events: nothing flows through the event engine, Quick Issue, or the records
 * surfaces.
 *
 * `capabilities.policyLibrary` is the routing flag (same pattern as
 * attendance's `capabilities.register`): domain-aware surfaces that enumerate
 * domains for per-student recording must skip any domain with
 * `capabilities.policyLibrary.enabled`, and the domain tile links to
 * `capabilities.policyLibrary.route` / `.adminRoute` instead of records.
 *
 * The real data lives in:
 *   schools/{schoolId}/policies/{policyId}            — policy heads
 *   …/policies/{policyId}/versions/{versionId}        — PDF versions + lifecycle
 *   …/versions/{versionId}/acks/{uid}                 — read-mode acknowledgements
 *   schools/{schoolId}/governingBody/{uid}            — approval group members
 *   signableDocuments/{docId}                         — signing (existing stack)
 *
 * `primary` below exists ONLY because assertDomainShape requires every domain
 * to declare a headline field. No event is ever recorded against it — the
 * policyLibrary capability routes all surfaces away before any input renders.
 */

import { assertDomainShape } from '../schemaAssert.js';
import { buildDomainCapabilities, buildDomainSurfaces } from '../centralModel.js';

export const POLICIES_DOMAIN_ID = 'policies';

// Placeholder — never rendered. See module doc above.
const PRIMARY = {
  id: 'status',
  label: 'Status',
  type: 'multiChoice',
  options: [
    { value: 'active',   label: 'Active',   color: 'success' },
    { value: 'archived', label: 'Archived', color: 'neutral' },
  ],
};

export const policiesDomainConfig = assertDomainShape({
  name: 'policies',
  label: 'Policies',
  description: 'School policy library — versioned PDFs, governing-body approval, parent and staff signing',
  protected: false,
  active: true,
  sortOrder: 9,
  icon: null,
  color: 'primary',
  defaultInputMethod: 'individual',
  defaultView: 'list',
  collections: {
    events: 'policies',
  },
  eventsCollection: 'policies',
  capabilities: buildDomainCapabilities({
    recording: false,
    policyLibrary: {
      enabled: true,
      route: '/policies',
      adminRoute: '/policies-admin',
    },
  }),
  surfaces: buildDomainSurfaces({
    quickIssue: false,
    queue: false,
    records: false,
    rules: false,
    standalone: { enabled: true, route: '/policies' },
  }),
  primary: PRIMARY,
  followUp: [],
  terminology: {
    event:    { label: 'policy',          plural: 'policies',          description: 'An official school policy document' },
    category: { label: 'category',        plural: 'categories',        description: 'Policy grouping — conduct, safety, academic, …' },
    type:     { label: 'policy type',     plural: 'policy types',      description: '' },
    action:   { label: 'revision',        plural: 'revisions',         description: 'A new version of a policy' },
    rule:     { label: 'rule',            plural: 'rules',             description: '' },
    queue:    { label: 'signing queue',   plural: 'signing queues',    description: '' },
  },
  contextTags: {},
  scope: { grades: [] },
});

export default policiesDomainConfig;
